/**
 * Surface gravity and orbital periods of the Sun, the planets, the Moon and Pluto.
 *
 * Source: NASA Goddard Space Flight Center, Planetary Fact Sheets
 * (https://nssdc.gsfc.nasa.gov/planetary/factsheet/), each body's own sheet read in
 * October 2026.
 *
 * - `gravity` is the sheet's mean surface gravity in m/s² (at the 1-bar level for the four
 *   giant planets; the Sun's is the sheet's equatorial value at the visible surface; the
 *   Moon's is its surface gravity).
 * - `siderealYear` is the sidereal orbit period around the Sun in Earth days: the time to
 *   return to the same place against the stars. The Moon has none.
 *
 * Earth's reference gravity here is the standard value g₀ = 9.80665 m/s² (exact, the value
 * that defines the kilogram-force and the pound-force); NASA's mean surface gravity of
 * 9.82 m/s² and the 9.78 to 9.83 spread between equator and pole are noted on its sheet.
 */

export const STANDARD_GRAVITY = 9.806_65;

export type BodyId =
  | "sun"
  | "mercury"
  | "venus"
  | "earth"
  | "moon"
  | "mars"
  | "jupiter"
  | "saturn"
  | "uranus"
  | "neptune"
  | "pluto";

export interface BodyData {
  id: BodyId;
  /** Surface gravity, m/s². */
  gravity: number;
  /** Sidereal orbit period around the Sun, Earth days; null for the Sun and the Moon. */
  siderealYear: number | null;
  /** The Sun and the gas and ice giants have no solid surface to stand on. */
  solidSurface: boolean;
}

export const BODIES: readonly BodyData[] = [
  { id: "sun", gravity: 274.0, siderealYear: null, solidSurface: false },
  { id: "mercury", gravity: 3.7, siderealYear: 87.969, solidSurface: true },
  { id: "venus", gravity: 8.87, siderealYear: 224.701, solidSurface: true },
  {
    id: "earth",
    gravity: STANDARD_GRAVITY,
    siderealYear: 365.256,
    solidSurface: true,
  },
  { id: "moon", gravity: 1.62, siderealYear: null, solidSurface: true },
  { id: "mars", gravity: 3.73, siderealYear: 686.98, solidSurface: true },
  {
    id: "jupiter",
    gravity: 25.92,
    siderealYear: 4332.589,
    solidSurface: false,
  },
  {
    id: "saturn",
    gravity: 11.19,
    siderealYear: 10_755.699,
    solidSurface: false,
  },
  { id: "uranus", gravity: 9.01, siderealYear: 30_685.4, solidSurface: false },
  {
    id: "neptune",
    gravity: 11.27,
    siderealYear: 60_189.018,
    solidSurface: false,
  },
  { id: "pluto", gravity: 0.62, siderealYear: 90_560, solidSurface: true },
];

export function findBody(id: string): BodyData | null {
  return BODIES.find((body) => body.id === id) ?? null;
}

export type WeightUnit = "kg" | "lb" | "N";

const KG_PER_LB = 0.453_592_37;

/** Mass in kilograms for a weight read on Earth in the given unit. */
export function massKg(amount: number, unit: WeightUnit): number {
  if (unit === "lb") {
    return amount * KG_PER_LB;
  }
  if (unit === "N") {
    return amount / STANDARD_GRAVITY;
  }
  return amount;
}

export interface BodyWeight {
  id: BodyId;
  gravity: number;
  /** Surface gravity as a multiple of Earth's standard gravity. */
  ratio: number;
  /** The weight in the unit the reader entered (what an Earth-calibrated scale would show). */
  weight: number;
  /** The force on the body in newtons. */
  newtons: number;
}

export function weightOn(
  body: BodyData,
  amount: number,
  unit: WeightUnit
): BodyWeight {
  const ratio = body.gravity / STANDARD_GRAVITY;
  return {
    id: body.id,
    gravity: body.gravity,
    ratio,
    weight: amount * ratio,
    newtons: massKg(amount, unit) * body.gravity,
  };
}

/** How high the same take-off speed carries you: height scales as 1/g. */
export function jumpHeight(earthHeight: number, body: BodyData): number {
  return (earthHeight * STANDARD_GRAVITY) / body.gravity;
}
